import type { AutoTargetGroup, Placement } from "@adpilot/shared";
import { IconInfoCircle } from "@tabler/icons-react";
import type { FieldPath, useForm } from "react-hook-form";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";
import type { CampaignCreationFormValues } from "@/lib/campaign-creation-state";
import { cn } from "@/lib/utils";

export type CampaignCreationForm = ReturnType<typeof useForm<CampaignCreationFormValues>>;

export function ConfigurationFieldRow({
  id,
  label,
  description,
  required = false,
  info = false,
  active = false,
  children,
}: {
  id?: string;
  label: string;
  description?: string;
  required?: boolean;
  info?: boolean;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      id={id}
      data-slot="configuration-field-row"
      tabIndex={id ? -1 : undefined}
      className={cn("scroll-mt-4 px-6 py-4", active && "focus-visible:bg-muted/20")}
    >
      <div className="max-w-4xl">
        <h3 className="flex items-center gap-1 text-[13px] font-semibold text-foreground">
          {required ? <span className="text-destructive-foreground">*</span> : null}
          {label}
          {info ? <IconInfoCircle className="size-3.5 text-muted-foreground" /> : null}
        </h3>
        {description ? (
          <p className="mt-1 text-xs leading-5 text-muted-foreground">{description}</p>
        ) : null}
      </div>
      <div className="mt-3 min-w-0">{children}</div>
    </section>
  );
}

export function RadioOption({
  name,
  value,
  checked,
  onChange,
  label,
  description,
  disabled = false,
  boxed = false,
}: {
  name: string;
  value: string;
  checked: boolean;
  onChange: (value: string) => void;
  label: string;
  description?: string;
  disabled?: boolean;
  boxed?: boolean;
}) {
  return (
    <label
      className={cn(
        "flex cursor-pointer items-start gap-2 text-[13px]",
        boxed && "rounded border border-line px-3 py-3",
        boxed && checked && "border-primary bg-primary/8",
        disabled && "cursor-not-allowed opacity-45",
      )}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange(value)}
        className="mt-0.5 size-4 accent-primary"
      />
      <span className="min-w-0">
        <span className={cn("block font-medium", checked && "text-primary")}>{label}</span>
        {description ? (
          <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">
            {description}
          </span>
        ) : null}
      </span>
    </label>
  );
}

export function TextField({
  name,
  label,
  form,
  type = "text",
  inputMode,
  suffix,
  prefix,
  placeholder,
  hideLabel = false,
  disabled = false,
  controlClassName,
}: {
  name:
    | keyof CampaignCreationFormValues
    | `placementAdjustments.${Placement}`
    | `groupBids.${AutoTargetGroup}`;
  label: string;
  form: CampaignCreationForm;
  type?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  suffix?: string;
  prefix?: string;
  placeholder?: string;
  hideLabel?: boolean;
  disabled?: boolean;
  controlClassName?: string;
}) {
  const fieldPath = name as FieldPath<CampaignCreationFormValues>;
  const fieldError = form.getFieldState(fieldPath, form.formState).error;
  const isInvalid = Boolean(fieldError?.message);
  return (
    <Field className="w-full max-w-[350px]" data-invalid={isInvalid || undefined}>
      <FieldLabel htmlFor={`campaign-${name}`} className={hideLabel ? "sr-only" : undefined}>
        {label}
      </FieldLabel>
      {prefix || suffix ? (
        <InputGroup className={controlClassName}>
          <InputGroupInput
            id={`campaign-${name}`}
            type={type}
            size="sm"
            inputMode={inputMode}
            disabled={disabled}
            placeholder={placeholder}
            aria-invalid={isInvalid || undefined}
            {...form.register(name as never)}
            className="text-xs"
          />
          {prefix ? (
            <InputGroupAddon align="inline-start">
              <InputGroupText>{prefix}</InputGroupText>
            </InputGroupAddon>
          ) : null}
          {suffix ? (
            <InputGroupAddon align="inline-end">
              <InputGroupText>{suffix}</InputGroupText>
            </InputGroupAddon>
          ) : null}
        </InputGroup>
      ) : (
        <Input
          id={`campaign-${name}`}
          type={type}
          size="sm"
          inputMode={inputMode}
          disabled={disabled}
          placeholder={placeholder}
          aria-invalid={isInvalid || undefined}
          {...form.register(name as never)}
          className={cn("text-xs", controlClassName)}
        />
      )}
      {fieldError?.message ? <FieldError>{fieldError.message}</FieldError> : null}
    </Field>
  );
}
